/** Subscriptions to the simulation.
 *
 * The rule (D8): React subscribes to `state.version` and to per-collection
 * counters, never to object identity. A `PlayerObservation` rebuilds its typed
 * arrays on every call, so a component that depended on identity would re-render
 * on every frame and a component that depended on deep equality would cost more
 * than the render it saved. A monotonic integer is both cheap and exact.
 */

import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { ContentRegistry, PlayerObservation } from "@game/core";
import { useStore } from "./store.js";

/** The single subscription primitive. Everything else derives from it. */
export function useVersion(): number {
  const store = useStore();
  const subscribe = useCallback((l: () => void) => store.subscribe(l), [store]);
  const get = useCallback(() => store.getVersion(), [store]);
  return useSyncExternalStore(subscribe, get, get);
}

/** The observation for the current version. Stable between versions. */
export function useObservation(): PlayerObservation {
  const store = useStore();
  const version = useVersion();
  return useMemo(() => store.getObservation(), [store, version]);
}

/**
 * A derived value, recomputed only when the observation changes.
 * `select` must be a module-level function or wrapped in `useCallback`; an
 * inline arrow defeats the memo and is a bug, not a style preference.
 */
export function useSelector<T>(select: (obs: PlayerObservation) => T): T {
  const obs = useObservation();
  return useMemo(() => select(obs), [obs, select]);
}

/**
 * Primitive counters. A panel that only cares "did the hero list change" takes
 * these as its memo key instead of the observation, so adding a resource does
 * not re-lay-out the hero strip.
 */
export interface Counters {
  readonly version: number;
  readonly heroes: number;
  readonly towns: number;
  readonly objects: number;
  readonly day: number;
  readonly activePlayer: string;
}

export function useCounters(): Counters {
  const obs = useObservation();
  return useMemo(
    () => ({
      version: obs.version,
      heroes: obs.heroes.length,
      towns: obs.towns.length,
      objects: obs.objects.length,
      day: obs.turn.day,
      activePlayer: obs.turn.activePlayer as string,
    }),
    [obs],
  );
}

/**
 * The content bundle, or null while it is still loading. Re-read on every
 * version bump: a host that finishes loading content mid-game is expected to
 * bump the version, and one that loads it before the first render (the normal
 * case) is served on the first read.
 */
export function useContent(): ContentRegistry | null {
  const store = useStore();
  const version = useVersion();
  return useMemo(() => store.getContent(), [store, version]);
}
